import bases from 'bases';
import moment from 'moment';
import { React, PT, fetchApi } from '@gasbuddy/react';
import JSONTree from 'react-json-tree';
import { Table, Button } from 'semantic-ui-react';
import { Auth } from '../..';
import { Role } from '../../../roles';
import { EditPhoneModal } from './EditPhoneModal';
import { EditNameModal } from './EditNameModal';
import { ConfirmUpdateButton } from './ConfirmUpdateButton';

function accountIdToLastName(id) {
  return `A${bases.toAlphabet(id, 'ABCDEFGHIJKLMNOPQRSTUVWXYZ')}`;
}

function profileName(uri, instance) {
  if (uri === 'gb:address') {
    if (instance === '') {
      return 'Address';
    }
    return `Address (${instance})`;
  } if (uri === 'gb:contact') {
    if (instance === '') {
      return 'Contact';
    }
    return `Contact (${instance})`;
  } if (uri === 'gb:phone') {
    if (instance === '') {
      return 'Phone';
    }
    return `${instance} Phone`;
  } if (uri === 'gb:loyalty') {
    if (instance === 'fr') {
      return 'Fuel Rewards';
    } if (instance === 'pfj') {
      return 'myRewards Plus';
    }
    return `Rewards Connect ${instance}`;
  }
  return `${uri}#${instance}`;
}

function profileDisplay(uri, value) {
  if (uri === 'gb:address') {
    const { locality, region, postal_code: zip, country } = value;
    return (
      <div>
        {locality}
,
        {region}
        {' '}
        {zip}
        {' '}
        {country}
      </div>
    );
  } if (uri === 'gb:contact') {
    const { first_name: fn, last_name: ln } = value;
    return (
      <div>
        {fn}
        {' '}
        {ln}
      </div>
    );
  } if (uri === 'gb:phone') {
    return <div>{value.raw || value.number}</div>;
  }
  if (uri === 'gb:loyalty') {
    return (
      <JSONTree
        inverted
        shouldExpandNode={() => false}
        data={value}
      />
    );
  }
  return JSON.stringify(value, null, '\t');
}

function clean(namespace, ident) {
  if (namespace === 'third_party') {
    const thirdParty = (ident.match(/([^:]+):/) || [])[1];
    const redactedThirdParties = [
      'yodlee',
      'fuelrewards',
    ];

    if (redactedThirdParties.includes(thirdParty)) {
      return `${thirdParty} id redacted`;
    }
  }

  return ident;
}

async function deleteIdentifier(id, namespace, ident, onUpdate) {
  await fetchApi({
    url: `/identity/user/${id}/identifiers/${namespace}/${ident}/delete`,
    body: {},
  });
  onUpdate();
}

async function deletePwdTag(id, onUpdate) {
  await fetchApi({
    url: `/identity/user/${id}/delete-password-tag`,
    body: {},
  });
  onUpdate();
}

async function addRegulatoryPreferenceTag(id, value, onUpdate) {
  await fetchApi({
    url: `/identity/user/${id}/opt-out-from-sale`,
  });
  onUpdate();
}


export const BasicInfo = ({ identifiers, profile, tags, id, guid, tripTakerGuids, deleteRequested, onUpdate }) => {
  const optOutTag = tags?.find(t => t.type === 'regulatory_preference' && t.value.startsWith('opt_out_from_sale'));

  return (
    <Table basic="very">
      <Table.Body>
        {deleteRequested && id > 0 && (
        <Table.Row key="deletion-requested" error={!!deleteRequested.created_at}>
          <Table.Cell colSpan="2">
            {`Deletion Requested on ${moment(deleteRequested.created_at).format('MM/DD/YYYY HH:mm:ss')}`}
            <br />
            {`status: ${deleteRequested.status}`}
          </Table.Cell>
        </Table.Row>
        )}
        <Table.Row key="raw-id">
          <Table.Cell width={3}>Account Id</Table.Cell>
          <Table.Cell>{id}</Table.Cell>
        </Table.Row>
        <Table.Row key="raw-guid">
          <Table.Cell width={3}>Guid</Table.Cell>
          <Table.Cell>{guid}</Table.Cell>
        </Table.Row>
        {tripTakerGuids.slice(0, 3).map(g => (
          <Table.Row key={`trip-taker-guid${g}`}>
            <Table.Cell width={3}>Trip Taker Guid</Table.Cell>
            <Table.Cell>{g}</Table.Cell>
          </Table.Row>
        ))}
        {tripTakerGuids.length > 3 && (
        <Table.Row key="trip-taker-more">
          <Table.Cell width={3}>Hidden Trip Takers</Table.Cell>
          <Table.Cell>{tripTakerGuids.length - 3}</Table.Cell>
        </Table.Row>
        )}
        <Table.Row key="wex-card-name">
          <Table.Cell width={3}>Wex Card</Table.Cell>
          <Table.Cell>{accountIdToLastName(id)}</Table.Cell>
        </Table.Row>
        {identifiers.map(i => (
          <Table.Row
            error={!!i.released_at}
            warning={!!i.deleted_at && !i.released_at}
            key={`${i.name}${i.identifier}`}
          >
            <Table.Cell width={3}>{i.name}</Table.Cell>
            <Table.Cell>{clean(i.name, i.identifier)}</Table.Cell>
            {i.name === 'email' && !i.released_at && (
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <Button basic color="red" onClick={() => deleteIdentifier(id, i.name, i.identifier, onUpdate)}>
              X
              </Button>
            </Auth>
            )}
          </Table.Row>
        ))}
        {profile.map(({ uri, instance, value }) => (
          <Table.Row key={`${uri}#${instance}`}>
            <Table.Cell width={3}>{profileName(uri, instance)}</Table.Cell>
            <Table.Cell>
              {uri === 'gb:phone' && (
              <EditPhoneModal onUpdate={onUpdate} accountId={id} instance={instance} />
              )}
              {uri === 'gb:contact' && (
              <EditNameModal onUpdate={onUpdate} accountId={id} first={value.first} last={value.last} />
              )}
              {profileDisplay(uri, value)}
            </Table.Cell>
          </Table.Row>
        ))}
        {id && !profile.find(p => (p.uri === 'gb:phone' && p.instance === 'billing')) && (
        <Table.Row>
          <Table.Cell width={3}>Billing Phone</Table.Cell>
          <Table.Cell><EditPhoneModal onUpdate={onUpdate} accountId={id} instance="billing" buttonText="add" /></Table.Cell>
        </Table.Row>
        )}
        {id && !profile.find(p => p.uri === 'gb:contact') && (
        <Table.Row>
          <Table.Cell width={3}>Contact/Name</Table.Cell>
          <Table.Cell><EditNameModal onUpdate={onUpdate} accountId={id} buttonText="add" /></Table.Cell>
        </Table.Row>
        )}
        {tags?.find(t => t.type === 'generated_password') && (
        <Table.Row>
          <Table.Cell colSpan="2">
            <Button basic onClick={() => deletePwdTag(id, onUpdate)}>Enable Password Entry</Button>
          </Table.Cell>
        </Table.Row>
      )}
        {optOutTag && (
        <Table.Row>
          <Table.Cell width={2}>Opted Out from Sale</Table.Cell>
          <Table.Cell>
            {optOutTag.value === 'opt_out_from_sale:ca' ? 'CCPA' : 'IN APP'}
          </Table.Cell>
        </Table.Row>
        )}
        <Table.Row>
          <Table.Cell colSpan="2">
            <ConfirmUpdateButton
              text="CCPA : Opt Out From Sale"
              confirm="Has this user been approved for Opt Out From Sale?"
              updater={() => addRegulatoryPreferenceTag(id, 'opt_out_from_sale:ca', onUpdate)}
            />
          </Table.Cell>
        </Table.Row>

      </Table.Body>
    </Table>
  );
};

BasicInfo.propTypes = {
  id: PT.number,
  guid: PT.string,
  tripTakerGuids: PT.arrayOf(PT.string),
  identifiers: PT.arrayOf(PT.shape({ name: PT.string, identifier: PT.string })),
  profile: PT.arrayOf(PT.shape({ uri: PT.string, instance: PT.string, value: PT.object })),
  tags: PT.arrayOf(PT.shape({ type: PT.string, value: PT.string })),
  deleteRequested: PT.shape({ status: PT.string, created_at: PT.string }),
  onUpdate: PT.func,
};

BasicInfo.defaultProps = {
  id: 0,
  guid: null,
  tripTakerGuids: [],
  identifiers: [],
  profile: [],
  tags: [],
  deleteRequested: {},
  onUpdate: null,
};
